import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getCreatorTrust } from '../../../../api_handlers/discovery';

const NAVY = '#111936';
const GOLD = '#FEBD1C';

const CHECK_LABELS = {
  audience_is_real: 'Audience is real',
  platform_verified: 'Platform verified',
};
const AXIS_LABELS = {
  audience_authenticity: 'Audience authenticity',
  commercial_hygiene: 'Commercial hygiene',
};
const AXIS_SUB = {
  audience_authenticity: 'is the following genuine',
  commercial_hygiene: 'sponsorship load & disclosure',
};

// Final, system-decided verdict tiers.
const VERDICT = {
  trusted: { color: '#0f7a3d', bg: '#e7f6ed', label: 'Trusted' },
  reliable: { color: '#1c5fd6', bg: '#e8f0fe', label: 'Reliable' },
  needs_review: { color: '#8a6d1a', bg: '#fff6df', label: 'Needs a look' },
  high_risk: { color: '#b3261e', bg: '#fdecea', label: 'High risk' },
};

// Controversy / politics / protest signal.
const SENSITIVITY = {
  clean: { color: '#0f7a3d', bg: '#e7f6ed', label: 'Clean' },
  low: { color: '#5a6172', bg: '#eef1f5', label: 'Low' },
  elevated: { color: '#8a6d1a', bg: '#fff6df', label: 'Elevated' },
  high: { color: '#b3261e', bg: '#fdecea', label: 'High' },
  unknown: { color: '#5a6172', bg: '#eef1f5', label: 'No data' },
};

function StatusChip({ status }) {
  const map = {
    pass: { icon: <CheckCircleIcon sx={{ fontSize: 15 }} />, c: '#127a3e', bg: '#e7f6ed', t: 'Pass' },
    fail: { icon: <CancelIcon sx={{ fontSize: 15 }} />, c: '#b3261e', bg: '#fdecea', t: 'Fail' },
    info: { icon: <InfoOutlinedIcon sx={{ fontSize: 15 }} />, c: '#5a6172', bg: '#eef1f5', t: 'Info' },
    unverified: { icon: <InfoOutlinedIcon sx={{ fontSize: 15 }} />, c: '#8a6d1a', bg: '#fff4d6', t: 'No data' },
  };
  const m = map[status] || map.info;
  return (
    <Chip
      size="small"
      icon={m.icon}
      label={m.t}
      sx={{ color: m.c, bgcolor: m.bg, fontWeight: 700, '& .MuiChip-icon': { color: m.c } }}
    />
  );
}

function Evidence({ items }) {
  if (!items || !items.length) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      {items.map((e, i) => (
        <Typography key={i} sx={{ fontSize: 11.5, color: '#475467', lineHeight: 1.4 }}>
          • {e.note}
          {e.ref && e.type === 'post' ? (
            <>
              {' '}
              — <a href={e.ref} target="_blank" rel="noreferrer" style={{ color: NAVY }}>{e.ref}</a>
            </>
          ) : null}
        </Typography>
      ))}
    </Box>
  );
}

export default function CreatorTrustModal({ open, setOpen, id }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = () => {
    if (!open || !id) return;
    setLoading(true);
    setError(null);
    setData(null);
    getCreatorTrust(id)
      .then((res) => setData(res))
      .catch((e) => setError(e?.response?.data?.error || 'Could not load the trust assessment.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [open, id]);

  const v = data ? VERDICT[data.verdict] || VERDICT.needs_review : null;
  const score = data && data.trust_score != null ? data.trust_score : null;

  return (
    <Dialog open={!!open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, fontWeight: 800, color: NAVY }}>
        Creator Trust
        <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error">{error}</Alert>}

        {data && !error && (
          <>
            {/* final verdict banner */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: v.bg,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {score != null && (
                <Box
                  sx={{
                    width: 62,
                    height: 62,
                    borderRadius: '50%',
                    border: `3px solid ${v.color}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                    bgcolor: '#fff',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 20, color: v.color, lineHeight: 1 }}>
                    {score}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#667', letterSpacing: 0.3 }}>/ 100</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, color: v.color }}>
                    {data.verdict_label || v.label}
                  </Typography>
                  {data.regulated_category ? (
                    <Chip size="small" label="Regulated category" sx={{ bgcolor: NAVY, color: '#fff' }} />
                  ) : null}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: '#3a4152', mt: 0.5 }}>{data.headline}</Typography>
              </Box>
            </Box>

            {/* checks the system decided from real data */}
            <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>Checks</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#667', mb: 1 }}>
              Decided automatically from the data on record. A failed audience check is a hard no on its own.
            </Typography>
            {Object.entries(data.checks || {}).map(([k, g]) => (
              <Box key={k} sx={{ py: 1, borderBottom: '1px solid #eef1f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 13, flex: 1 }}>
                    {CHECK_LABELS[k] || k}
                  </Typography>
                  <StatusChip status={g.status} />
                </Box>
                <Evidence items={g.evidence} />
              </Box>
            ))}

            {/* trust axes */}
            <Typography sx={{ fontWeight: 800, color: NAVY, mt: 2.5, mb: 0.5 }}>Signals</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#667', mb: 1 }}>
              The measurable signals behind the score, shown separately.
            </Typography>
            {Object.entries(data.axes || {}).map(([k, a]) => (
              <Box key={k} sx={{ py: 1, borderBottom: '1px solid #eef1f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>
                      {AXIS_LABELS[k] || k}
                      <Typography component="span" sx={{ fontSize: 11, color: '#98a2b3', ml: 0.5 }}>
                        ({AXIS_SUB[k]})
                      </Typography>
                    </Typography>
                  </Box>
                  {a.score == null ? (
                    <Chip size="small" label="No data" sx={{ bgcolor: '#eef1f5', color: '#5a6172', fontWeight: 700 }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 130, justifyContent: 'flex-end' }}>
                      <Box sx={{ width: 70, height: 6, borderRadius: 3, bgcolor: '#eee', overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.max(0, Math.min(100, a.score))}%`, height: '100%', bgcolor: GOLD }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: 13 }}>{a.score}</Typography>
                    </Box>
                  )}
                </Box>
                <Evidence items={a.evidence} />
              </Box>
            ))}

            {/* controversy / politics / protest — a separate brand-safety read */}
            {data.content_sensitivity ? (
              (() => {
                const cs = data.content_sensitivity;
                const s = SENSITIVITY[cs.level] || SENSITIVITY.unknown;
                return (
                  <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 800, color: NAVY }}>
                        Controversy &amp; politics
                      </Typography>
                      <Chip
                        size="small"
                        label={s.label}
                        sx={{ color: s.color, bgcolor: s.bg, fontWeight: 700 }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: '#667', mb: 1 }}>
                      Political, protest or controversy content in recent posts — a brand-safety
                      read, scanned automatically from captions and hashtags.
                    </Typography>
                    {cs.categories && cs.categories.length ? (
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                        {cs.categories.map((c) => (
                          <Chip
                            key={c}
                            size="small"
                            label={c.replace('_', ' / ')}
                            sx={{ bgcolor: '#f0f1f5', color: '#3a4152', textTransform: 'capitalize' }}
                          />
                        ))}
                      </Box>
                    ) : null}
                    <Evidence items={cs.evidence} />
                  </Box>
                );
              })()
            ) : null}

            {/* category fit — explicitly NOT a trust measure */}
            {data.category_fit ? (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f6f7fb' }}>
                <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 13 }}>
                  Category fit: {data.category_fit.primary_category || '—'}
                  <Chip size="small" label="not a trust measure" sx={{ ml: 1, bgcolor: '#e4e7ee', color: '#475467' }} />
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#667', mt: 0.5 }}>{data.category_fit.note}</Typography>
              </Box>
            ) : null}

            {/* honest footnote — what automated vetting does not cover */}
            {data.not_covered && data.not_covered.length ? (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 700, color: '#5a6172', fontSize: 12 }}>
                  Not covered by automated vetting
                </Typography>
                {data.not_covered.map((n) => (
                  <Typography key={n.key} sx={{ fontSize: 11.5, color: '#8a90a0', mt: 0.5, lineHeight: 1.4 }}>
                    • <b>{n.label}:</b> {n.note}
                  </Typography>
                ))}
              </Box>
            ) : null}

            <Typography sx={{ fontSize: 11, color: '#98a2b3', mt: 2 }}>
              Assessed {data.assessed_at ? new Date(data.assessed_at).toLocaleString() : '—'}
              {data.next_due_at ? ` · re-runs by ${new Date(data.next_due_at).toLocaleDateString()}` : ''}
            </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
